import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Grid,
  Alert,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  EventNote as LeaveIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useSnackbar } from "notistack";
import {
  getMyAttendance,
  getEmployeeAttendance,
  updateAttendance,
  selectMyAttendanceRecords,
  selectEmployeeAttendanceRecords,
  selectAttendanceLoading,
  selectAttendanceError,
  selectAttendancePagination,
  clearError,
} from "../../../store/slices/attendanceSlice";
import { selectUser } from "../../../store/slices/authSlice";
import { useAuth } from "../auth/AuthGuard";
import { ROUTES, ATTENDANCE_STATUS, ROLES } from "../../../constants";
import attendanceAPI from "../../../services/api/attendance.api";

const AttendanceList = ({ viewMode = "personal" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(selectUser);
  const { hasAnyRole } = useAuth();

  const myAttendanceRecords = useSelector(selectMyAttendanceRecords);
  const employeeAttendanceRecords = useSelector(
    selectEmployeeAttendanceRecords
  );
  const loading = useSelector(selectAttendanceLoading);
  const error = useSelector(selectAttendanceError);
  const pagination = useSelector(selectAttendancePagination);

  // Set activeTab based on viewMode
  const [activeTab, setActiveTab] = useState(viewMode === "employee" ? 1 : 0);

  // Update activeTab when viewMode changes
  useEffect(() => {
    setActiveTab(viewMode === "employee" ? 1 : 0);
  }, [viewMode]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [entryTypeFilter, setEntryTypeFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const isManager = hasAnyRole([
    ROLES.ADMIN,
    ROLES.HR_MANAGER,
    ROLES.DEPARTMENT_HEAD,
  ]);

  useEffect(() => {
    // Load data immediately when component mounts
    loadAttendanceData();
  }, [dispatch, activeTab, selectedEmployee]);

  // Load employee data when switching to Employee Records tab
  useEffect(() => {
    if (activeTab === 1 && isManager) {
      const params = { page: 1, limit: 100 };
      if (monthFilter) params.month = monthFilter;
      if (yearFilter) params.year = yearFilter;
      if (statusFilter) params.status = statusFilter;
      dispatch(getEmployeeAttendance({ employeeId: "all", params }));
    }
  }, [activeTab, isManager, dispatch]);

  // Also load data when component first mounts
  useEffect(() => {
    // Force load all records on initial mount based on viewMode
    if (viewMode === "personal") {
      dispatch(
        getMyAttendance({
          page: 1,
          limit: 100,
        })
      );
    } else if (viewMode === "employee" && isManager) {
      // Load all employees' attendance
      dispatch(
        getEmployeeAttendance({
          employeeId: "all",
          params: { page: 1, limit: 100 },
        })
      );
    }
  }, [dispatch, viewMode, isManager]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      dispatch(clearError());
    }
  }, [error, enqueueSnackbar, dispatch]);

  const buildParams = () => {
    const params = { page: 1, limit: 100 };
    if (monthFilter) params.month = monthFilter;
    if (yearFilter) params.year = yearFilter;
    if (statusFilter) params.status = statusFilter;
    return params;
  };

  const loadAttendanceData = () => {
    const params = buildParams();
    if (activeTab === 0) {
      dispatch(getMyAttendance(params));
    } else if (activeTab === 1 && isManager) {
      dispatch(getEmployeeAttendance({ employeeId: "all", params }));
    }
  };

  const handleRefresh = () => {
    loadAttendanceData();
    enqueueSnackbar("Attendance data refreshed", { variant: "success" });
  };

  const handleApplyFilters = () => {
    const params = buildParams();
    if (activeTab === 0) {
      dispatch(getMyAttendance(params));
    } else if (activeTab === 1 && isManager) {
      dispatch(getEmployeeAttendance({ employeeId: "all", params }));
    }
    enqueueSnackbar("Filters applied", { variant: "info" });
  };

  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleViewRecord = () => {
    if (selectedRecord) {
      // Navigate to detailed view or open modal
      console.log("View record:", selectedRecord);
    }
    handleMenuClose();
  };

  const handleEditRecord = () => {
    if (selectedRecord) {
      // Open edit modal or navigate to edit page
      console.log("Edit record:", selectedRecord);
    }
    handleMenuClose();
  };

  const handleExportData = async () => {
    try {
      const params = {
        month: monthFilter,
        year: yearFilter,
        status: statusFilter || undefined,
        employee_id: activeTab === 1 ? selectedEmployee : undefined,
      };

      const blob = await attendanceAPI.exportAttendanceReport(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `attendance_report_${yearFilter}_${monthFilter}.xlsx`;
      link.click();

      window.URL.revokeObjectURL(url);
      enqueueSnackbar("Report exported successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to export report", { variant: "error" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case ATTENDANCE_STATUS.PRESENT:
        return "success";
      case ATTENDANCE_STATUS.ABSENT:
        return "error";
      case ATTENDANCE_STATUS.ON_LEAVE:
        return "info";
      case ATTENDANCE_STATUS.HALF_DAY:
        return "warning";
      default:
        return "default";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "Not set";

    try {
      // Handle ISO datetime strings (e.g., "2025-08-29T00:39:00.000Z")
      if (timeString.includes("T")) {
        const date = new Date(timeString);
        if (isNaN(date.getTime())) return "Invalid time";

        // Format as 12-hour time with AM/PM
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } else {
        // Time-only format (e.g., "00:39:00")
        const time = timeString.slice(0, 5);
        const [hours, minutes] = time.split(":");
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      }
    } catch (error) {
      console.error("Error formatting time:", error, { timeString });
      return "Invalid time";
    }
  };

  const calculateWorkingHours = (arrivalTime, departureTime) => {
    if (!arrivalTime || !departureTime) return "Not available";

    try {
      // Handle ISO datetime strings (e.g., "2025-08-29T00:39:00.000Z")
      let arrival, departure;

      if (arrivalTime.includes("T")) {
        // ISO datetime format
        arrival = new Date(arrivalTime);
        departure = new Date(departureTime);
      } else {
        // Time-only format (e.g., "00:39:00")
        arrival = new Date(`2000-01-01T${arrivalTime}`);
        departure = new Date(`2000-01-01T${departureTime}`);
      }

      const diffMs = departure - arrival;

      // Check if the calculation is valid
      if (isNaN(diffMs) || diffMs < 0) return "Invalid duration";

      const totalMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      // Format in a human-readable way
      if (hours === 0) {
        return `${minutes} min`;
      } else if (minutes === 0) {
        return `${hours} hr`;
      } else {
        return `${hours} hr ${minutes} min`;
      }
    } catch (error) {
      console.error("Error calculating working hours:", error, {
        arrivalTime,
        departureTime,
      });
      return "Calculation error";
    }
  };

  const columns = [
    {
      field: "date",
      headerName: "Date",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">
            {new Date(params.value).toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
          {params.row.manual_entry && (
            <Tooltip title="Manually entered" arrow>
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ fontWeight: "bold" }}
              >
                ⚠
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "arrival_time",
      headerName: "Clock In",
      width: 100,
      renderCell: (params) => formatTime(params.value),
    },
    {
      field: "departure_time",
      headerName: "Clock Out",
      width: 100,
      renderCell: (params) => formatTime(params.value),
    },
    {
      field: "working_hours",
      headerName: "Hours",
      width: 100,
      renderCell: (params) =>
        calculateWorkingHours(
          params.row.arrival_time,
          params.row.departure_time
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={params.value}
            color={getStatusColor(params.value)}
            size="small"
          />
          {params.row.manual_entry && (
            <Tooltip title="Manual Entry" arrow>
              <Chip
                label="M"
                color="warning"
                size="small"
                variant="outlined"
                sx={{ minWidth: 20, height: 20, fontSize: "0.7rem" }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "manual_entry",
      headerName: "Entry Type",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Manual" : "Automatic"}
          color={params.value ? "warning" : "success"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "activity",
      headerName: "Activity",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "manual_entry_reason",
      headerName: "Manual Entry Reason",
      width: 200,
      renderCell: (params) => {
        if (params.row.manual_entry && params.value) {
          return (
            <Tooltip title={params.value} arrow>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "warning.main",
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          );
        }
        return null;
      },
    },
    {
      field: "manual_entry_at",
      headerName: "Manual Entry Date",
      width: 180,
      renderCell: (params) => {
        if (params.row.manual_entry && params.value) {
          return new Date(params.value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
        return null;
      },
    },
    {
      field: "leave_application",
      headerName: "Leave Reference",
      width: 180,
      sortable: false,
      renderCell: (params) => {
        if (params.row.leaveApplication && params.row.leaveApplication.id) {
          const leave = params.row.leaveApplication;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip
                title={`Leave Application #${leave.id} - ${leave.leaveType?.name || "Leave"}`}
                arrow
              >
                <Chip
                  icon={<LeaveIcon />}
                  label={`Leave #${leave.id}`}
                  size="small"
                  color="info"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/leaves/${leave.id}`);
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "info.light",
                      color: "white",
                    },
                  }}
                />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            —
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(event) => handleMenuOpen(event, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Add employee name column for manager view
  if (activeTab === 1 && isManager) {
    columns.unshift({
      field: "employee_name",
      headerName: "Employee",
      width: 150,
      renderCell: (params) =>
        params.row.user?.first_name + " " + params.row.user?.sur_name,
    });
  }

  const attendanceData =
    activeTab === 0 ? myAttendanceRecords : employeeAttendanceRecords;

  const filteredData = attendanceData.filter((record) => {
    // Only apply search filter if search term is entered
    const matchesSearch =
      searchTerm === "" ||
      record.activity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.date?.toLowerCase().includes(searchTerm.toLowerCase());

    // Only apply status filter if explicitly selected
    const matchesStatus = statusFilter === "" || record.status === statusFilter;

    // Only apply entry type filter if explicitly selected
    const matchesEntryType =
      entryTypeFilter === "" ||
      (entryTypeFilter === "manual" && record.manual_entry) ||
      (entryTypeFilter === "automatic" && !record.manual_entry);

    return matchesSearch && matchesStatus && matchesEntryType;
  });

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const statusOptions = [
    { value: ATTENDANCE_STATUS.PRESENT, label: "Present" },
    { value: ATTENDANCE_STATUS.ABSENT, label: "Absent" },
    { value: ATTENDANCE_STATUS.ON_LEAVE, label: "On Leave" },
    { value: ATTENDANCE_STATUS.HALF_DAY, label: "Half Day" },
  ];

  const tabLabels = isManager
    ? ["My Attendance", "Employee Records"]
    : ["My Attendance"];

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            {viewMode === "employee"
              ? "Employee Attendance Records"
              : "My Attendance Records"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {viewMode === "employee"
              ? "View and manage all employees' attendance records"
              : "View your attendance history"}
          </Typography>
          {viewMode === "employee" && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Note: Now showing all employees' attendance records from the
              backend.
            </Alert>
          )}
          {attendanceData.length === 0 && !loading && (
            <Alert severity="info" sx={{ mt: 1 }}>
              No attendance records found. Click "Load All Records" to fetch
              data.
            </Alert>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<TimelineIcon />}
            onClick={() => navigate(ROUTES.ATTENDANCE)}
          >
            Clock In/Out
          </Button>

          {viewMode === "personal" && (
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(ROUTES.ATTENDANCE_EMPLOYEE_RECORDS)}
            >
              Employee Records
            </Button>
          )}

          {viewMode === "employee" && (
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(ROUTES.ATTENDANCE_MY_RECORDS)}
            >
              My Records
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              if (activeTab === 0) {
                dispatch(getMyAttendance({ page: 1, limit: 100 }));
              } else if (activeTab === 1 && isManager) {
                dispatch(
                  getEmployeeAttendance({
                    employeeId: "all",
                    params: { page: 1, limit: 100 },
                  })
                );
              }
            }}
          >
            Force Refresh
          </Button>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (activeTab === 0) {
                dispatch(
                  getMyAttendance({
                    page: 1,
                    limit: 100,
                  })
                );
              } else if (activeTab === 1 && isManager) {
                dispatch(
                  getEmployeeAttendance({
                    employeeId: "all",
                    params: { page: 1, limit: 100 },
                  })
                );
              }
            }}
            sx={{ ml: 1 }}
          >
            Load All Records
          </Button>

          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={handleExportData}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      {isManager && (
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const currentMonth = new Date().getMonth() + 1;
                  const currentYear = new Date().getFullYear();
                  setSearchTerm("");
                  setStatusFilter("");
                  setEntryTypeFilter("");
                  setMonthFilter(currentMonth);
                  setYearFilter(currentYear);
                  if (activeTab === 1) setSelectedEmployee("all");
                  // Re-fetch with cleared filters
                  const params = { page: 1, limit: 100, month: currentMonth, year: currentYear };
                  if (activeTab === 0) {
                    dispatch(getMyAttendance(params));
                  } else if (activeTab === 1 && isManager) {
                    dispatch(getEmployeeAttendance({ employeeId: "all", params }));
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={yearFilter}
                onChange={(e) => setYearFilter(parseInt(e.target.value))}
                inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Entry Type"
                value={entryTypeFilter}
                onChange={(e) => setEntryTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Entries</MenuItem>
                <MenuItem value="automatic">Automatic</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
              </TextField>
            </Grid>

            {activeTab === 1 && isManager && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Employee"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  helperText="All Employees: Shows all employees' attendance records"
                >
                  <MenuItem value="all">All Employees</MenuItem>
                  {/* This would be populated from employee API */}
                  <MenuItem value="1">John Doe</MenuItem>
                  <MenuItem value="2">Jane Smith</MenuItem>
                  <MenuItem value="3">Bob Wilson</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      {filteredData.length > 0 && (
        <Card
          sx={{
            mb: 4,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1a237e",
                  fontSize: "1.25rem",
                }}
              >
                Summary Statistics
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Showing {filteredData.length} of {attendanceData.length} records
                {filteredData.length !== attendanceData.length && (
                  <Chip
                    label="Filtered"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #e3f2fd",
                  }}
                >
                  <Typography
                    variant="h4"
                    color="primary.main"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {filteredData.length}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Total Records
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f1f8e9",
                    border: "2px solid #c8e6c9",
                  }}
                >
                  <Typography
                    variant="h4"
                    color="success.main"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {
                      filteredData.filter((record) => !record.manual_entry)
                        .length
                    }
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Automatic Entries
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fff8e1",
                    border: "2px solid #ffecb3",
                  }}
                >
                  <Typography
                    variant="h4"
                    color="warning.main"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {
                      filteredData.filter((record) => record.manual_entry)
                        .length
                    }
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Manual Entries
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#e3f2fd",
                    border: "2px solid #bbdefb",
                  }}
                >
                  <Typography
                    variant="h4"
                    color="info.main"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {
                      filteredData.filter(
                        (record) => record.status === "present"
                      ).length
                    }
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Present Days
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Attendance Table */}
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Box sx={{ height: 600 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Loading attendance records...
              </Typography>
            </Box>
          ) : attendanceData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: 3,
                p: 4,
              }}
            >
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                No attendance records found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                Click the button below to load your attendance data
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (activeTab === 0) {
                    dispatch(
                      getMyAttendance({
                        page: 1,
                        limit: 100,
                      })
                    );
                  } else if (activeTab === 1 && isManager) {
                    dispatch(
                      getEmployeeAttendance({
                        employeeId: "all",
                        params: { page: 1, limit: 100 },
                      })
                    );
                  }
                }}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                }}
              >
                Load Records
              </Button>
            </Box>
          ) : filteredData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: 3,
                p: 4,
              }}
            >
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                No records match the current filters
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                Try adjusting your search criteria or clear all filters
              </Typography>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setEntryTypeFilter("");
                  if (activeTab === 1) {
                    setSelectedEmployee("all");
                  }
                }}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderWidth: 2,
                }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
             <DataGrid
               rows={filteredData.map((record) => ({
                 ...record,
                 id: record.id || Math.random(), // Ensure each row has a unique ID
               }))}
               columns={columns}
               loading={loading}
               pageSize={25}
               rowsPerPageOptions={[10, 25, 50, 100]}
               disableSelectionOnClick
               getRowClassName={(params) => {
                 // Highlight rows that are linked to leave applications
                 if (params.row.leaveApplication && params.row.leaveApplication.id) {
                   return 'leave-linked-row';
                 }
                 return '';
               }}
               sx={{
                 border: "none",
                 "& .MuiDataGrid-cell:focus": {
                   outline: "none",
                 },
                 "& .MuiDataGrid-columnHeaders": {
                   backgroundColor: "#f5f5f5",
                   borderBottom: "2px solid #e0e0e0",
                 },
                 "& .MuiDataGrid-row:hover": {
                   backgroundColor: "#f8f9fa",
                 },
                 "& .MuiDataGrid-row.leave-linked-row": {
                   backgroundColor: "#e3f2fd",
                   "&:hover": {
                     backgroundColor: "#bbdefb",
                   },
                 },
                 "& .MuiDataGrid-cell": {
                   borderBottom: "1px solid #f0f0f0",
                 },
               }}
             />
          )}
        </Box>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewRecord}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {isManager && (
          <MenuItem onClick={handleEditRecord}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Record</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* No Data Message */}
      {!loading && filteredData.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No attendance records found for the selected filters.
        </Alert>
      )}
    </Box>
  );
};

export default AttendanceList;
